// Shared prize-tier math for raffle events — used by the Sorteios page and the
// lobby entry card so the pool/winners/top-prize are computed one way.

type PrizeReward = { kind?: string; amount?: number };
type PrizeTier = { count?: number; amount?: number; rewards?: PrizeReward[] };
type PrizeBracket = { min_n?: number; max_n?: number; tiers?: PrizeTier[] };

export type PrizeRow = { count: number; amount: number };

function firstBracket(brackets: unknown): PrizeBracket | null {
  if (!Array.isArray(brackets) || brackets.length === 0) return null;
  return brackets[0] as PrizeBracket;
}

// A tier's cash is its top-level amount, or — for composite/bundle tiers that
// hold cash inside rewards[] — the sum of the cash rewards.
export function tierCash(tier: PrizeTier): number {
  const amount = Number(tier.amount || 0);
  if (amount > 0) return amount;
  return (tier.rewards || []).reduce(
    (sum, reward) => sum + ((reward.kind || 'cash').toLowerCase() === 'cash' ? Number(reward.amount || 0) : 0),
    0,
  );
}

export function poolOf(brackets: unknown): number {
  const b = firstBracket(brackets);
  if (!b?.tiers) return 0;
  return b.tiers.reduce((sum, tier) => sum + Number(tier.count || 0) * tierCash(tier), 0);
}

// Programs have several brackets (e.g. 10-24 / 25-49 / 50-100 entries), each
// with its own payout — settlement picks the one `total_entries` falls into
// (`JdbcRaffleRepository.appliedBracket`), not always brackets[0]. This is the
// same selection `WalletPage.tsx`'s `raffleBracket` already does; falls back
// to brackets[0] when nothing matches (legacy events with no min_n/max_n).
function bracketByEntries(brackets: unknown, totalEntries: number): PrizeBracket | null {
  if (!Array.isArray(brackets) || brackets.length === 0) return null;
  const match = brackets.find((bracket: PrizeBracket) => {
    const min = Number(bracket?.min_n || 0);
    const max = Number(bracket?.max_n || Number.MAX_SAFE_INTEGER);
    return totalEntries >= min && totalEntries <= max;
  });
  return (match || brackets[0]) as PrizeBracket;
}

// Pool for the bracket `total_entries` actually falls into — used only as a
// fallback (see `settledPoolOf`) when there are no winner records yet to sum.
export function poolByEntries(brackets: unknown, totalEntries: number): number {
  const b = bracketByEntries(brackets, totalEntries);
  if (!b?.tiers) return 0;
  return b.tiers.reduce((sum, tier) => sum + Number(tier.count || 0) * tierCash(tier), 0);
}

// The money actually paid out for a settled draw: the sum of `winners[].amount`
// — checkable against the very list of winners rendered on screen, unlike a
// bracket picked by `total_entries` alone (three brackets exist; `poolOf` only
// ever reads brackets[0], which is correct for an *upcoming* draw where N is
// still unknown, but wrong once the draw has settled into a specific bracket).
// Falls back to the bracket-by-entries pool only when there are no winner
// records at all (e.g. a voided draw's caller, or data still materializing).
export function settledPoolOf(
  winners: Array<{ amount?: number }> | undefined,
  brackets: unknown,
  totalEntries: number,
): number {
  const list = winners || [];
  if (list.length > 0) {
    return list.reduce((sum, winner) => sum + Number(winner.amount || 0), 0);
  }
  return poolByEntries(brackets, totalEntries);
}

export function countWinners(brackets: unknown): number {
  const b = firstBracket(brackets);
  if (!b?.tiers) return 0;
  return b.tiers.reduce((sum, tier) => sum + Number(tier.count || 0), 0);
}

// Flatten the prize tiers into "{count} winners get R${amount}" rows, biggest
// prize first — so a player can see the top prize and the full split.
export function prizeRows(brackets: unknown): PrizeRow[] {
  const b = firstBracket(brackets);
  if (!b?.tiers) return [];
  return b.tiers
    .map((tier) => ({ count: Number(tier.count || 0), amount: tierCash(tier) }))
    .filter((row) => row.count > 0 && row.amount > 0)
    .sort((a, b) => b.amount - a.amount);
}
